import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api'
import { useDebounce } from '@/hooks/use-debounce'
import { Loader2, MapPin, X } from 'lucide-react'

// Types
interface AddressPrediction {
  place_id: string
  description: string
  structured_formatting: {
    main_text: string
    secondary_text: string
  }
  types: string[]
}

export interface AddressDetails {
  formatted_address: string
  street: string
  city: string
  state: string
  state_code: string
  postal_code: string
  country: string
  country_code: string
  lat?: number
  lng?: number
}

interface AddressAutocompleteProps {
  /** Unique ID for accessibility */
  id: string
  /** Label text */
  label: string
  /** Current street value */
  value: string
  /** Called when address is selected with all parsed fields */
  onAddressSelect: (address: AddressDetails) => void
  /** Called when street input changes (for controlled input) */
  onStreetChange?: (street: string) => void
  /** Restrict to specific countries (e.g., ['us', 'ca']) */
  countryRestrictions?: string[]
  /** Placeholder text */
  placeholder?: string
  /** Error message to display */
  error?: string
  /** Whether the field is required */
  required?: boolean
  /** Additional class names */
  className?: string
  /** Disable the input */
  disabled?: boolean
  /** Aria described by ID for additional context */
  ariaDescribedBy?: string
}

/**
 * AddressAutocomplete - WCAG 2.2 AA compliant address autocomplete component
 *
 * Features:
 * - Full keyboard navigation (Arrow keys, Enter, Escape)
 * - Screen reader announcements via live region
 * - Proper ARIA attributes for combobox pattern
 * - Debounced API calls (300ms)
 * - Session tokens for Google billing optimization
 */
export function AddressAutocomplete({
  id,
  label,
  value,
  onAddressSelect,
  onStreetChange,
  countryRestrictions,
  placeholder = 'Start typing an address...',
  error,
  required = false,
  className,
  disabled = false,
  ariaDescribedBy,
}: AddressAutocompleteProps) {
  // Local state
  const [inputValue, setInputValue] = React.useState(value)
  const [isOpen, setIsOpen] = React.useState(false)
  const [activeIndex, setActiveIndex] = React.useState(-1)
  const [sessionToken] = React.useState(() => crypto.randomUUID())
  const [announcement, setAnnouncement] = React.useState('')

  // Refs
  const inputRef = React.useRef<HTMLInputElement>(null)
  const listboxRef = React.useRef<HTMLUListElement>(null)

  // Debounce input for API calls
  const debouncedInput = useDebounce(inputValue, 300)

  // Sync external value changes
  React.useEffect(() => {
    setInputValue(value)
  }, [value])

  // Fetch autocomplete predictions
  const {
    data: predictions,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['places-autocomplete', debouncedInput, countryRestrictions],
    queryFn: async () => {
      if (!debouncedInput || debouncedInput.length < 3) {
        return { predictions: [] }
      }

      const params = new URLSearchParams({
        input: debouncedInput,
        sessiontoken: sessionToken,
      })

      if (countryRestrictions?.length) {
        params.set('components', countryRestrictions.map((c) => `country:${c}`).join('|'))
      }

      const response = await api.get(`/places/autocomplete?${params}`)
      return response.data
    },
    enabled: debouncedInput.length >= 3 && isOpen,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    // Address autocomplete is non-critical: a failed lookup (e.g. a Places 5xx)
    // must degrade to manual entry, NOT escalate to the global error boundary
    // (App.tsx throwOnError on 5xx). Overrides that default for this query only.
    throwOnError: false,
  })

  const predictionsList: AddressPrediction[] = predictions?.predictions || []

  // Announce results to screen readers
  React.useEffect(() => {
    if (isOpen && predictionsList.length > 0) {
      setAnnouncement(
        `${predictionsList.length} address suggestion${predictionsList.length === 1 ? '' : 's'} available. Use arrow keys to navigate.`
      )
    } else if (isOpen && debouncedInput.length >= 3 && !isLoading && predictionsList.length === 0) {
      setAnnouncement('No address suggestions found.')
    }
  }, [predictionsList.length, isOpen, debouncedInput, isLoading])

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
    onStreetChange?.(newValue)
    setIsOpen(true)
    setActiveIndex(-1)
  }

  // Handle address selection
  const handleSelect = async (prediction: AddressPrediction) => {
    const mainText = prediction.structured_formatting?.main_text || prediction.description
    setInputValue(mainText)
    setIsOpen(false)
    setAnnouncement(`Selected: ${prediction.description}`)

    try {
      // Fetch full address details
      const params = new URLSearchParams({
        place_id: prediction.place_id,
        sessiontoken: sessionToken,
      })

      const response = await api.get(`/places/details?${params}`)
      const details: AddressDetails = response.data

      onAddressSelect(details)
    } catch (error) {
      console.error('Failed to fetch address details:', error)
      // Still call with partial data
      onAddressSelect({
        formatted_address: prediction.description,
        street: mainText,
        city: '',
        state: '',
        state_code: '',
        postal_code: '',
        country: '',
        country_code: '',
      })
    }
  }

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || predictionsList.length === 0) {
      if (e.key === 'ArrowDown' && inputValue.length >= 3) {
        setIsOpen(true)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveIndex((prev) => (prev < predictionsList.length - 1 ? prev + 1 : 0))
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : predictionsList.length - 1))
        break
      case 'Enter': {
        e.preventDefault()
        const selectedPrediction = predictionsList[activeIndex]
        if (activeIndex >= 0 && activeIndex < predictionsList.length && selectedPrediction) {
          handleSelect(selectedPrediction)
        }
        break
      }
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        setActiveIndex(-1)
        inputRef.current?.focus()
        break
      case 'Tab':
        setIsOpen(false)
        setActiveIndex(-1)
        break
    }
  }

  // Close on outside click
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(e.target as Node) &&
        listboxRef.current &&
        !listboxRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Scroll active item into view
  React.useEffect(() => {
    if (activeIndex >= 0 && listboxRef.current) {
      const activeElement = listboxRef.current.children[activeIndex] as HTMLElement
      activeElement?.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIndex])

  const showDropdown = isOpen && (predictionsList.length > 0 || isLoading)
  const listboxId = `${id}-listbox`
  const errorId = `${id}-error`

  return (
    <div className={cn('relative', className)}>
      <Label htmlFor={id}>
        {label}
        {required && (
          <span className="text-destructive ml-1" aria-hidden="true">
            *
          </span>
        )}
      </Label>

      <div className="relative mt-1.5">
        <div className="relative">
          <MapPin
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            ref={inputRef}
            id={id}
            type="text"
            role="combobox"
            aria-expanded={showDropdown}
            aria-haspopup="listbox"
            aria-controls={listboxId}
            aria-activedescendant={activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined}
            aria-autocomplete="list"
            aria-required={required}
            aria-invalid={!!error}
            aria-describedby={cn(error && errorId, ariaDescribedBy) || undefined}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => inputValue.length >= 3 && setIsOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(
              'pl-10 pr-10',
              error && 'border-destructive focus-visible:ring-destructive'
            )}
            autoComplete="off"
          />

          {/* Loading indicator */}
          {(isLoading || isFetching) && (
            <Loader2
              className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground"
              aria-hidden="true"
            />
          )}

          {/* Clear button */}
          {inputValue && !isLoading && !isFetching && (
            <button
              type="button"
              onClick={() => {
                setInputValue('')
                onStreetChange?.('')
                inputRef.current?.focus()
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Clear address"
            >
              <X className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
            </button>
          )}
        </div>

        {/* Dropdown listbox */}
        {showDropdown && (
          <ul
            ref={listboxRef}
            id={listboxId}
            role="listbox"
            aria-label="Address suggestions"
            className={cn(
              'absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg',
              'max-h-60 overflow-auto',
              'py-1'
            )}
          >
            {isLoading ? (
              <li className="px-3 py-2 text-sm text-muted-foreground">Searching addresses...</li>
            ) : predictionsList.length === 0 ? (
              <li className="px-3 py-2 text-sm text-muted-foreground">No addresses found</li>
            ) : (
              predictionsList.map((prediction, index) => (
                <li
                  key={prediction.place_id}
                  id={`${id}-option-${index}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={cn(
                    'px-3 py-2 cursor-pointer text-sm',
                    'hover:bg-accent hover:text-accent-foreground',
                    'focus:bg-accent focus:text-accent-foreground',
                    index === activeIndex && 'bg-accent text-accent-foreground'
                  )}
                  onClick={() => handleSelect(prediction)}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <div className="font-medium">
                    {prediction.structured_formatting?.main_text || prediction.description}
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {prediction.structured_formatting?.secondary_text}
                  </div>
                </li>
              ))
            )}
          </ul>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p id={errorId} className="text-sm text-destructive mt-1" role="alert">
          {error}
        </p>
      )}

      {/* Live region for screen reader announcements */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
    </div>
  )
}
